import axios from 'axios';
import { DateTime } from 'luxon';
import numeral from "numeral";
import snakeCaseKeys from 'snakecase-keys';
import Vue from 'vue';
import { Route } from 'vue-router';
import Constants from './constants';
import router from './router/router';
import store from './store/store';
import { PingVO } from './views/biz/login/vo/login.vo';
import { MainNotificationsVO } from './vo/common.vo';


export default class CommonUtils { 

    static exchangeRateSyncInterval = 10 * 60 * 1000; // 10분
    static lastExchangeRateSyncedTime = new Date().getTime();
    static exchangeRate = 0;

    static logger = axios.create({ baseURL: process.env.VUE_APP_BASE_URL_LOG, withCredentials: true });

    /**
     * 로컬 스토리지 객체 취득
     * @param key 
     */
    static getLocalStorageObject(key: string) {
        const obj = localStorage.getItem(key);
        if (!obj) {
            return null;
        }
        return JSON.parse(obj);
    }

    /**
     * 로컬 스토리지 객체 저장
     * @param key 
     * @param val 
     */
    static putLocalStorageObject(key: string, val: object): void {
        localStorage.setItem(key, JSON.stringify(val));
    }

    /**
     * 로컬 스토리지 객체 제거
     * @param key
     */
    static removeLocalStorageObject(key: string): void {
        localStorage.removeItem(key);
    }

    /**
     * 세션 스토리지 객체 취득
     * @param key 
     */
    static getSessionStorageObject(key: string) {
        const obj = sessionStorage.getItem(key);
        if (!obj) {
            return null;
        }
        return JSON.parse(obj);
    }

    /**
     * 세션 스토리지 객체 저장
     * @param key 
     * @param val 
     */
    static putSessionStorageObject(key: string, val: object): void {
        sessionStorage.setItem(key, JSON.stringify(val));
    }

    // 환율
    static getExchangeRate(): number {
        if ((new Date().getTime() - CommonUtils.lastExchangeRateSyncedTime) < CommonUtils.exchangeRateSyncInterval && CommonUtils.exchangeRate) {
            return CommonUtils.exchangeRate;
        }
        CommonUtils.lastExchangeRateSyncedTime = new Date().getTime();
        axios.get('/common/exchange-rate')
            .then((r) => {
                CommonUtils.exchangeRate = r.data.sub / r.data.main;
            });
        return CommonUtils.exchangeRate;
    }

    // 메인 통화 -> 서브 통화 (ex: krw -> usd)
    static toSubCurrency(mainCurrency: number) {
        return mainCurrency * CommonUtils.getExchangeRate();
    }
    // 정수로
    static toInt(price: number, format = "0,0") {
        return numeral(price).format(format)
    }    
    // 금액 포맷
    static toNumber(price: number, format = "0,0[.]00") {
        return numeral(price).format(format)
    }
    // 금액 포맷
    static toPrice(price: number, format = process.env.VUE_APP_SYSTEM_LOCALE == 'ko' ? "0,0[.]00" : "0,0.00") {
        return numeral(price).format(format)
    }

    // splash 화면 
    static isNewCustomer(): boolean {
        const userAgent = navigator.userAgent.toLowerCase();
        if (userAgent.indexOf('zikoo') == -1 || userAgent.indexOf('chiclyn') == -1) { // 앱이 아니면 확인 노필요
            return false; // ㅂㅂ
        }

        const newCustomer = localStorage.getItem(Constants.NEW_CUSTOMER);

        // 앱심사때문에 cn앱은 워크스루 화면이 제외됐었으나 개인정보,약관 동의 레이어팝업땜시 워크스루 화면 살림
        // if(newCustomer == '0' || process.env.VUE_APP_SYSTEM_LOCALE == 'cn' ) {
        if (newCustomer == '0') {
            return false;
        } else {
            return true;
        }
    }

    // 현재 로케일이 이거임?
    static isSystemLocale(localeStr: string) {
        return process.env.VUE_APP_SYSTEM_LOCALE == localeStr;
    }

    // static url 생성
    static createStaticUrl(vue: Vue, routeName: string) {
        const path = vue.$router.resolve({ name: routeName });

        return window.location.origin + path.href;
    }

    // 앱임?
    static isApp(): boolean {
        return window['ReactNativeWebView'] ? true : false;
    }
    // 운영임?
    static isProduction(): boolean {
        return process.env.NODE_ENV == "production";
    }

    // 브라우저 타이틀
    static setDocumentTitle(titles?: Array<string>): void {
        if (!titles || !titles.length) {
            document.title = process.env.VUE_APP_TITLE || '';
            return;
        }
        const title = titles.join(" - ") + ' - ' + process.env.VUE_APP_TITLE;

        const titleEl = document.querySelector('head meta[property="og:title"]') as Element;

        document.title = title;
        titleEl.setAttribute('content', title);
    }

    // META description (SEO)
    static setMetaDiscription(discriptionText?: string) {
        const descEl = document.querySelector('head meta[name="description"]') as Element;
        const ogDescEl = document.querySelector('head meta[property="og:description"]') as Element;

        if (!discriptionText) {
            descEl.setAttribute('content', process.env.VUE_APP_DESCRIPTION || '');
            ogDescEl.setAttribute('content', process.env.VUE_APP_DESCRIPTION || '');
            return;
        }

        descEl.setAttribute('content', discriptionText + " / " + process.env.VUE_APP_DESCRIPTION_SUFFIX);
        ogDescEl.setAttribute('content', discriptionText + " / " + process.env.VUE_APP_DESCRIPTION_SUFFIX);

    }
    // ADD HEAD tag Link (SEO) 
    static setHeadLink(link?: string){
        const linkEl = document.querySelector('head link[rel="canonical"]') as Element;

        if(!link){
            linkEl.setAttribute('href',process.env.VUE_APP_MAIN_URL||'');
        }

        linkEl.setAttribute('href', process.env.VUE_APP_MAIN_URL+""+link);
    }
    // Meta Image (SEO) 
    static setMetaImage(imgUrl?: string){
        const metaImgEl = document.querySelector('head meta[property="og:image"]') as Element;

        if(!imgUrl){
            metaImgEl.setAttribute('content',process.env.VUE_APP_OG_IMAGE||'');
        }

        metaImgEl.setAttribute('content', imgUrl||'');
    }

    // GTag page_view 이벤트 전송 (zikoo)
    static sendPageViewEventLog(vue: any, route: Route) {  
        // 딥링크 때문에 만듬
       if(route.name=="BridgeDeeplink" || route.name=="OpenApp"){
        return;
       }

       const sample = {
        pageTitle: route.name,
        pageLocation: route.fullPath,
        pagePath: route.path
        }

       const [path, query = ""] = decodeURIComponent(decodeURIComponent(route.fullPath)).split("?");
       const eventParams: Record<string, string> = {
        pageTitle: sample.pageTitle ?? '',
        pagePath: path 
      };
       
       query.split("&").forEach((pair) => {
         const [k, v] = pair.split("=");
         if (["utm_source", "utm_medium", "utm_campaign", "utm_content"].includes(k)) {
           eventParams[k] = v;
         }
       });
       // 여기까지
 
        // 사용자 접근 로그 발송
        axios
        .post(`/common/logs/access`,{eventName: 'page_view',eventParams:sample,hashCode: localStorage.getItem("guest"),fullUrl:route.path, queryString: route.query,layoutNm: route.name})
        .then((val) =>{
        if(val.data){
            localStorage.setItem("guest",val.data);
        }
        });
    
        if (process.env.NODE_ENV != 'production' || CommonUtils.isSystemLocale('cn') == true) {
            return;
        }
        
        // 앱이면
        if (this.isApp() == true) {
            window["webViewBridge"].send("sendFirebasePageView", { eventParams }, null);
            return;
        }

        // 현재 스크린정보(라우트명) 전송
        vue.$analytics.setCurrentScreen(route.name as string, { global: true });
        // 페이지뷰 정보 전송
        vue.$analytics.logEvent('page_view', eventParams);
    
    }

    // GTag 공통 event_log 전송 (zikoo)
    static sendCommonEventLogs(vue: any, eventName: string, eventParams: any) {
        // 사용자 접근 로그 발송
        axios
        .post(`/common/logs/access`,{eventName: eventName,eventParams:eventParams,hashCode: localStorage.getItem("guest"),fullUrl:vue.$route.path, queryString: vue.$route.query,layoutNm: vue.$route.name})
        .then((val) =>{
        if(val.data){
            localStorage.setItem("guest",val.data);
        }
        });

        if (process.env.NODE_ENV != 'production' || CommonUtils.isSystemLocale('cn') == true) {
            return;
        }

        // 앱이면
        if (this.isApp() == true) {
            window["webViewBridge"].send("sendFirebaseAnalytics", { eventName, eventParams }, null);
            return;
        }

        // 웹 전송
        vue.$analytics.logEvent(eventName, eventParams);
        
    }
    /**
     * 모비온에 이벤트 보내기 
     * 1. index.html 에 스크립트삽입
     * 2. [특정 전환 이벤트 발생시 메소드 호출]
     * 3. router.ts 의 afterEach에서 pageview 전송
     */
    static sendMobionLogEvent(vars?: Record<string, any>){
        if (CommonUtils.isSystemLocale('cn') == true) {
            return;
        }
        const _window = window as any;
        // 페이지별 필요한 변수 삽입
        if (typeof _window.CallMtm !== "function") return;
        const payload = {...vars};

        try {
            _window.CallMtm(payload);  
            _window.callDM();
           
           //  console.log("[Enliple] CallMtm invoked", payload);
          } catch (e) {
            //console.error("[Enliple] CallMtm error", e, payload);
          }
    }
    static ensureCallDM(): void {       
        if (CommonUtils.isSystemLocale('cn') == true) {
            return;
        }
        const _window = window as any;
        // 페이지별 필요한 변수 삽입
       
        if (typeof _window.callDM !== "function") {
            _window.callDM = (...args: any[]): void => {
              (_window.callDM.q = _window.callDM.q || []).push(args);
            };
          }
       
        try {
            _window.callDM();
           
          } catch (e) {
            //console.error("[Enliple] CallMtm error", e, payload);
          }
      }
    /**
     * 네이버 로그 이벤트 보내기
     * 1. index.html 에 누더기 스크립트 삽입
     * 2. [ 특정 전환 이벤트 발생 시 요 메소드 호출 ]
     * 3. router.ts 의 afterEach에서 로그 전송
     * @param conversionType 1: 구매완료, 2: 회원가입, 3:장바구니담기, 4:신청/예약, 5:기타
     * @param etc 누더기
     */
    static sendNaverLogEvent(conversionType: number, etc: any) {
        if (CommonUtils.isSystemLocale('cn') == true) {
            return;
        }
        console.log("naver log1");

        const _window = (window as any);
        const wcs = _window.wcs;
        if (!_window._nasa) {
            _window._nasa = {};
        }

        _window._nasa["cnv"] = wcs.cnv(conversionType, etc);
  

        // 중복호출 막기위해 afterEach(라우터)에서 예외처리함
         wcs.inflow("zikoo.co.kr");
         CommonUtils.putLocalStorageObject("naver log",_window._nasa["cnv"]);
        _window.wcs_do(_window._nasa);
  
    }

    // 스네이크케이스 변환
    static toSnakeCase(vo: any) {
        return snakeCaseKeys(vo);
    }

    // 상품 찜하기
    static async zzimProduct(product: { productCd: number; isZzim: boolean }) {
        return axios
            .put(`/product/${product.productCd}/like`) 
            .then(() => {
                product.isZzim = !product.isZzim;
            })
    }

    // 현재 날짜 기준 시간 표시 (어디서 떠돌아 다니는거 개조함)
    // TODO 언어
    static toRelativeDate(regDate: string) {
        const locale: string = process.env.VUE_APP_SYSTEM_LOCALE != undefined ? process.env.VUE_APP_SYSTEM_LOCALE : 'ko';

        const dsc = {
            'ko' :  ['방금 전',' 분 전',' 시간 전',' 일 전',' 주 전',' 개월 전',' 년 전'],
            'global' :  ['just now',' minutes ago',' hours ago',' days ago',' weeks ago',' months ago',' years ago']
        };

        const from = DateTime.fromSQL(regDate);
        const remainingTimeDuration = from.diffNow();
        const seconds = remainingTimeDuration.as("second") * -1;
        if (seconds < 60) return dsc[locale][0];

        const minutes = remainingTimeDuration.as("minute") * -1;
        if (minutes < 60) return `${Math.floor(minutes)}`+dsc[locale][1];

        const hours = remainingTimeDuration.as("hour") * -1;
        if (hours < 24) return `${Math.floor(hours)}`+dsc[locale][2];

        const days = remainingTimeDuration.as("day") * -1;
        if (days < 7) return `${Math.floor(days)}`+dsc[locale][3];

        const weeks = remainingTimeDuration.as("week") * -1;
        if (weeks < 5) return `${Math.floor(weeks)}`+dsc[locale][4];

        const months = remainingTimeDuration.as("month") * -1;
        if (months < 12) return `${Math.floor(months)}`+dsc[locale][5];

        const years = remainingTimeDuration.as("year") * -1;
        return `${Math.round(years)}`+dsc[locale][6];
    }

    // 남은 시간
    static remainDate(endDate: string): {days: number; hours: number} {
        // 종료일이 없진 않을테지만 넣어놓음
        if(!endDate) {
            return {days: 0, hours: 0};
        }

        const end = DateTime.fromSQL(endDate);
        const start = DateTime.local();

        // 종료일 - 현재일 을 일하고 시간 나눔.
        const diff = end.diff(start, ['days', 'hours']);

        // 일, 시간 가져오기
        const days = diff['values']['days'];
        const hours = Math.round(diff['values']['hours']);

        return {days: days, hours: hours};

    }

    // 비밀번호 검증
    static validatePassword(password: string) {
        return /[a-zA-Z]/.test(password) == true && /[0-9]/.test(password) == true && /^.{8,20}$/.test(password) == true;
    }

    // 외부 쿼리스트링 처리
    static processExternalQueryString(params: any) {
        if (!params) {
            return;
        }

        if (params.refId) {
            this.putLocalStorageObject(Constants.EXTERNAL_PARAM_REF_MEM_CD, params.refMemCd);
        }
        if (params.refMemCd) {
            this.putLocalStorageObject(Constants.EXTERNAL_PARAM_REF_ID, params.refId);
        }
    }

    // 로그인 선체크
    static async checkLoginStatus(_this: Vue): Promise<PingVO> {
        let result = {} as PingVO;
        await axios.get("/login/v2/ping").then(r => {
            result = r.data as PingVO;
        });
        // 로그인 안하면 로그인화면으로
        if (!result.isLogin) {
            const params = _this.$route.params;
            params.toName = _this.$route.name as string;
            _this.$router.push({ name: "Login", params: params });
            return Promise.reject("pung");
        }
        return result;
    }

    static log(routerName: string | null | undefined, memCd: number, test: any) {
        // 운영에서만 사용함
        if(process.env.NODE_ENV != 'production') {
            return;
        }
        if (!test) {
            return;
        }
        test.rn = routerName;
        memCd && (test.mc = memCd);
        CommonUtils.logger.post("/log", test);
    }

    // 플랫폼별 이미지 분기처리
    // PC: IFNULL(PC이미지, Mobile이미지)
    // Mobile: Mobile이미지
    static getImageByPlatform(urls: { mobileImage: string; pcImage: string }) {
        if (Vue.prototype.$isMobile) {
            return urls.mobileImage;
        }
        return urls.pcImage || urls.mobileImage;
    }

    // 회원 정보 세션에 세팅
    static async setMemberInfoToSession() {
        return axios.get("/login/simple-details").then((r) => {
            console.log(r.data)
            if(r.data){
                Vue.prototype.$session.exists() || Vue.prototype.$session.start();
                Vue.prototype.$session.set(Constants.MEMBER_INFO, r.data);
            }
        });
    }

    static async logout(obj: any){
        // 로그아웃 하시겠습니까?
        if(confirm(obj.$t("common.c0003").toString()) == false){
            return;
        }

        return axios.get("/logout").then((r) => {
            if(obj.$route.name != "Main"){
                obj.$router.push({ name: "Main" });
            }

            obj.$session.destroy();
            obj.$store.dispatch("updateCartCount"); //장바구니 업데이트

            sessionStorage.removeItem(Constants.SESSION_LOGIN);
            obj.$store.commit("touchKeepAlive");
        });
    }

    // 뒤로가기
    static goBack() {
        console.log(store.state.fromRouteName)
        if (store.state.fromRouteName) {
            router.go(-1);
        } else {
            router.push({ name: "Main" });
        }
    }

    // 현재 년도 가져오기(copyright 거시기)
    static currentYear() {
      return new Date().getFullYear();
    }
}